// src/pages/LocaliteDesignSystem.js
import React, { useState } from 'react';
import {
  MapPin, Newspaper, Users, ShieldCheck, Buildings, Scales,
  CheckCircle, Warning, Info, XCircle, Lightning, Megaphone,
  ChartBar, Globe, Handshake, ArrowRight, CalendarBlank,
  MapTrifold, UserCircle, PushPin, Flag, Gavel,
  Leaf, ChatCircle, Bell, Heart, Sparkle
} from '@phosphor-icons/react';
import '../styles/LocaliteDesignSystem.css';

/**
 * Localite — Design System v2.1
 *
 * Bold, expressive civic tech design system. Purple-forward palette signifying
 * unity across parties. Sans-serif + monospace typography. Color-blocking over
 * thin lines. Hard-edged shadows, mixed corner radii, SVG patterns.
 *
 * Fonts: Space Grotesk (headings), Inter (body/UI), JetBrains Mono (badges, alerts, meta, code)
 * Icons: Phosphor Icons (@phosphor-icons/react) with "fill" weight
 */

// ============================================
// HandLogo — Handy Co Logo (SVG)
// ============================================
const HandLogo = ({ size = 24, className = '' }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 3000 3000"
    xmlns="http://www.w3.org/2000/svg"
    style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
  >
    <g transform="matrix(4.4244,0,0,4.4244,-763.512,-749.867)">
      <path
        d="M469.35,296.88C471.517,281.367 479.09,270.373 492.07,263.9C501.98,258.97 513.43,258.2 523.51,262.48C538.363,268.787 546.78,279.927 548.76,295.9C548.814,296.312 549.168,296.623 549.583,296.623C549.692,296.623 549.799,296.602 549.9,296.56C556.947,293.6 564,292.783 571.06,294.11C585.51,296.83 599.02,308.88 602.86,323.19C603.66,326.17 604.057,330.753 604.05,336.94C604.05,374.927 604.04,412.843 604.02,450.69C604.02,450.693 604.02,450.696 604.02,450.699C604.02,450.913 604.196,451.089 604.41,451.089C604.515,451.089 604.617,451.046 604.69,450.97C618.84,437.02 639.41,426.89 659.24,427.98C664.66,428.28 664.37,432.59 664.38,437.26C664.5,492.507 664.463,547.753 664.27,603C664.237,611.653 663.75,619.143 662.81,625.47C658.67,653.417 647.713,678.313 629.94,700.16C610.46,724.12 585.973,740.797 556.48,750.19C505.82,766.34 452.23,754.63 412.11,720.14C383.98,695.96 364.99,661.82 359.82,624.7C359.14,619.813 358.8,613.167 358.8,604.76C358.807,538.667 358.797,472.577 358.77,406.49C358.76,390.59 358.14,382.04 364.01,370.82C373.46,352.77 392.95,346.17 412.5,352.32C412.827,352.42 412.997,352.297 413.01,351.95C413.123,346.123 413.227,340.303 413.32,334.49C413.553,319.93 419.647,308.657 431.6,300.67C443.393,292.777 455.83,291.597 468.91,297.13C469.17,297.237 469.317,297.153 469.35,296.88ZM517.16,491.72C520.22,485.287 521.75,479.547 521.75,474.5C521.783,418.06 521.773,361.623 521.72,305.19C521.72,299.22 520.74,294.51 515.91,291.36C511.16,288.25 504.9,288.7 500.67,292.54C498.043,294.92 496.73,297.907 496.73,301.5C496.737,372.253 496.783,442.977 496.87,513.67C496.87,513.671 496.87,513.672 496.87,513.673C496.87,513.882 497.042,514.053 497.25,514.053C497.325,514.053 497.398,514.031 497.46,513.99C506.173,508.437 512.74,501.013 517.16,491.72ZM467.39,336.13C467.34,318.61 442.6,317.07 442.06,335.11C442.007,336.903 441.98,338.7 441.98,340.5C441.827,407.407 441.93,474.247 442.29,541.02C442.29,541.021 442.29,541.022 442.29,541.023C442.29,541.242 442.471,541.423 442.69,541.423C442.753,541.423 442.814,541.408 442.87,541.38L466.85,529.68C467.163,529.527 467.32,529.273 467.32,528.92C467.6,474.647 467.623,410.383 467.39,336.13ZM550.47,480.8C547.46,509.19 529.31,532.05 504.01,544.48C465.437,563.44 426.84,582.383 388.22,601.31C387.913,601.463 387.753,601.713 387.74,602.06C385.87,644.65 409.72,684.7 444.33,708.19C455.69,715.903 468.897,721.473 483.95,724.9C484.197,724.953 484.28,724.86 484.2,724.62C462.16,657.07 506.62,584.84 575.61,571.32C575.97,571.253 576.15,571.037 576.15,570.67C575.977,492.277 575.867,413.887 575.82,335.5C575.8,318.5 551.04,317.5 551,336.5C550.92,379.393 550.91,422.29 550.97,465.19C550.983,472.297 550.817,477.5 550.47,480.8ZM412.72,555.84C412.824,555.79 412.89,555.685 412.89,555.57C412.983,501.41 413.007,447.22 412.96,393C412.953,386.9 410.483,382.7 405.55,380.4C398.13,376.95 387.3,381.33 387.3,390.5C387.273,449.68 387.303,508.76 387.39,567.74C387.394,568.05 387.65,568.303 387.96,568.303C388.05,568.303 388.14,568.281 388.22,568.24L412.72,555.84ZM605.42,598.11C582.667,596.85 562.21,603.087 544.05,616.82C524.537,631.58 512.927,651.053 509.22,675.24C506.7,691.687 508.74,707.213 515.34,721.82C516.053,723.393 517.173,725.14 518.7,727.06C518.888,727.298 519.179,727.433 519.48,727.42C574.07,725.03 618.07,684.3 632.03,632.72C635.1,621.37 635.22,610.51 635.23,598.25C635.263,554.337 635.273,510.413 635.26,466.48C635.256,466.225 635.045,466.018 634.79,466.018C634.702,466.018 634.615,466.043 634.54,466.09C611.48,481.03 606.19,505.64 606.14,531.87C606.107,553.777 606.05,575.687 605.97,597.6C605.97,597.96 605.787,598.13 605.42,598.11Z"
        style={{ fill: 'currentColor', fillRule: 'nonzero' }}
      />
    </g>
  </svg>
);

// ============================================
// DESIGN TOKENS — Colors (updated: neutral foundation, pinker/saturated purple, vibrant variants)
// ============================================
const colorTokens = {
  foundation: [
    { name: 'Stone',          hex: '#F8F7F5', darkHex: '#171412', desc: 'Page background' },
    { name: 'Pebble',         hex: '#F0EEEB', darkHex: '#211E1B', desc: 'Subtle background' },
    { name: 'White',           hex: '#FFFFFF', darkHex: '#2A2724', desc: 'Surface' },
    { name: 'Chalk',           hex: '#FEFEFE', darkHex: '#33302D', desc: 'Raised surface' },
    { name: 'Driftwood',       hex: '#D8D4CE', darkHex: '#4A4540', desc: 'Border' },
    { name: 'Charcoal',        hex: '#1C1917', darkHex: '#F5F0EC', desc: 'Primary text' },
    { name: 'Walnut',          hex: '#4A4540', darkHex: '#C8C0B8', desc: 'Secondary text' },
    { name: 'Pewter',          hex: '#8A8580', darkHex: '#8A8580', desc: 'Muted text' },
  ],
  primary: [
    { name: 'Unity Purple',    hex: '#7B3FD4', desc: 'Primary brand' },
    { name: 'Purple Vibrant',  hex: '#9333EA', desc: 'Emphasis / vibrant accent' },
    { name: 'Purple Dark',     hex: '#6428B8', desc: 'Hover / pressed' },
    { name: 'Purple BG',       hex: '#F0E8FA', darkHex: '#2E2240', desc: 'Purple background' },
  ],
  secondary: [
    { name: 'Warm Amber',      hex: '#D69E2E', desc: 'Secondary — optimism' },
    { name: 'Amber Vibrant',   hex: '#F59E0B', desc: 'Vibrant amber accent' },
    { name: 'Amber BG',        hex: '#FFFBEB', darkHex: '#2A2515', desc: 'Amber background' },
    { name: 'Community Teal',  hex: '#2A9D8F', desc: 'Secondary — growth' },
    { name: 'Teal Vibrant',    hex: '#14B8A6', desc: 'Vibrant teal accent' },
    { name: 'Teal BG',         hex: '#E6F5F3', darkHex: '#152E2A', desc: 'Teal background' },
  ],
  tertiary: [
    { name: 'Civic Blue',      hex: '#3B6FB6', desc: 'Tertiary — tradition' },
    { name: 'Blue BG',         hex: '#E8F0FA', darkHex: '#1A2538', desc: 'Blue background' },
    { name: 'Civic Red',       hex: '#C5393A', desc: 'Tertiary — tradition' },
    { name: 'Red BG',          hex: '#FBE9E9', darkHex: '#381A1A', desc: 'Red background' },
  ],
};

// ============================================
// DESIGN TOKENS — Typography
// ============================================
const typographyTokens = [
  {
    name: 'Space Grotesk',
    family: "'Space Grotesk', -apple-system, sans-serif",
    usage: 'Headings & display',
    style: 'normal',
    weights: ['400 Regular', '500 Medium', '600 SemiBold', '700 Bold'],
    sample: 'Your neighborhood, your politics.',
  },
  {
    name: 'Inter',
    family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    usage: 'Body, UI, paragraphs',
    style: 'normal',
    weights: ['300 Light', '400 Regular', '500 Medium', '600 SemiBold', '700 Bold'],
    sample: 'The quick brown fox jumps over the lazy dog.',
  },
  {
    name: 'JetBrains Mono',
    family: "'JetBrains Mono', monospace",
    usage: 'Badges, alerts, meta, code',
    style: 'normal',
    weights: ['400 Regular', '500 Medium', '600 SemiBold'],
    sample: 'ACTIVE • IN_COMMITTEE • v2.1',
  },
];

// ============================================
// DESIGN TOKENS — Principles
// ============================================
const principles = [
  {
    icon: <Heart weight="fill" size={22} />,
    name: 'Human First',
    desc: 'Warm, organic design that treats residents as people — not data points. Bold color and type create an inviting, approachable civic experience.',
  },
  {
    icon: <Sparkle weight="fill" size={22} />,
    name: 'Expressive & Bold',
    desc: 'Color-blocking, strong typography, hard-edged shadows, and SVG patterns create visual energy. Every element has presence and personality.',
  },
  {
    icon: <Users weight="fill" size={22} />,
    name: 'Accessible to All',
    desc: 'Mobile-first, screen-reader friendly, high contrast. Civic information should reach every resident regardless of device or ability.',
  },
  {
    icon: <Handshake weight="fill" size={22} />,
    name: 'Unity Across Parties',
    desc: 'Purple as our primary color signifies non-partisan unity. Blue and red exist as tertiary accents — present but never dominant.',
  },
  {
    icon: <MapPin weight="fill" size={22} />,
    name: 'Locally Rooted',
    desc: 'Every design choice reflects the local community. City-specific data, geography-aware components, and neighborhood-level detail.',
  },
  {
    icon: <Lightning weight="fill" size={22} />,
    name: 'Confident Motion',
    desc: 'Purposeful animations with subtle bounce. Transitions feel alive and responsive — not clinical or robotic.',
  },
];

// ============================================
// DESIGN TOKENS — Icons
// ============================================
const iconShowcase = [
  { icon: <MapPin weight="fill" size={20} />, name: 'MapPin', usage: 'Locations, districts' },
  { icon: <Newspaper weight="fill" size={20} />, name: 'Newspaper', usage: 'Policy feed' },
  { icon: <Users weight="fill" size={20} />, name: 'Users', usage: 'Community, residents' },
  { icon: <Scales weight="fill" size={20} />, name: 'Scales', usage: 'Legislation, law' },
  { icon: <Gavel weight="fill" size={20} />, name: 'Gavel', usage: 'Votes, decisions' },
  { icon: <Buildings weight="fill" size={20} />, name: 'Buildings', usage: 'City, government' },
  { icon: <Megaphone weight="fill" size={20} />, name: 'Megaphone', usage: 'Announcements' },
  { icon: <Globe weight="fill" size={20} />, name: 'Globe', usage: 'Community, civic' },
  { icon: <Flag weight="fill" size={20} />, name: 'Flag', usage: 'Elections, milestones' },
  { icon: <Handshake weight="fill" size={20} />, name: 'Handshake', usage: 'Bipartisan, unity' },
  { icon: <ChartBar weight="fill" size={20} />, name: 'ChartBar', usage: 'Data, analytics' },
  { icon: <Bell weight="fill" size={20} />, name: 'Bell', usage: 'Notifications' },
  { icon: <CalendarBlank weight="fill" size={20} />, name: 'CalendarBlank', usage: 'Events, dates' },
  { icon: <ChatCircle weight="fill" size={20} />, name: 'ChatCircle', usage: 'Discussion' },
  { icon: <PushPin weight="fill" size={20} />, name: 'PushPin', usage: 'Pinned, important' },
  { icon: <UserCircle weight="fill" size={20} />, name: 'UserCircle', usage: 'Council members' },
];

// ============================================
// COMPONENT
// ============================================
const LocaliteDesignSystem = () => {
  const [isDark, setIsDark] = useState(false);
  const [toggleStates, setToggleStates] = useState({ notifications: true, digest: false });

  const toggleSwitch = (key) => {
    setToggleStates(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className={`loc-page${isDark ? ' loc-dark' : ''}`}>

      {/* ============ HEADER ============ */}
      <header className="loc-header">
        <div className="loc-header-left">
          <a href="https://handyco.dev" className="loc-hand-logo-link" aria-label="Handy Co">
            <HandLogo size={28} className="loc-hand-logo" />
          </a>
          <span className="loc-header-badge">Design System</span>
        </div>
        <button
          className="loc-theme-toggle"
          onClick={() => setIsDark(!isDark)}
          aria-label="Toggle theme"
        >
          <span>{isDark ? 'dark' : 'light'}</span>
          <div className="loc-theme-toggle-track">
            <div className="loc-theme-toggle-thumb" />
          </div>
        </button>
      </header>

      {/* ============ HERO (with subtle dot pattern) ============ */}
      <section className="loc-hero">
        <div className="loc-hero-logo">
          <MapPin weight="fill" size={36} />
        </div>
        <h1 className="loc-hero-title">Localite</h1>
        <p className="loc-hero-subtitle">{'civic_design_system // v2.1'}</p>
        <p className="loc-hero-description">
          A bold, human-centered design system for communicating local politics to residents.
          Expressive typography, hard-edged shadows, SVG patterns, and mobile-first layouts — starting with Nashville.
        </p>
      </section>

      {/* ============ CONTENT ============ */}
      <div className="loc-content">

        {/* Design Principles */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Design Principles</h2>
            <p className="loc-section-desc">
              The guiding values behind every Localite interface. Bold, warm, and unapologetically civic.
            </p>
          </div>
          <div className="loc-principles-grid">
            {principles.map((p, i) => (
              <div key={i} className="loc-principle-card">
                <div className="loc-principle-icon">{p.icon}</div>
                <div className="loc-principle-name">{p.name}</div>
                <div className="loc-principle-desc">{p.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Color Palette */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Color Palette</h2>
            <p className="loc-section-desc">
              Unity purple leads — slightly pink, fully saturated. Warm amber and community teal support
              with vibrant accents. Civic blue and red appear as respectful tertiary accents.
              Foundation colors are warm neutral — no purple tint.
            </p>
          </div>

          {Object.entries(colorTokens).map(([group, colors]) => (
            <div key={group} className="loc-color-group">
              <div className="loc-subsection-title">{group}</div>
              <div className="loc-swatches">
                {colors.map((c, i) => (
                  <div key={i} className="loc-swatch">
                    <div
                      className="loc-swatch-color"
                      style={{
                        backgroundColor: isDark && c.darkHex ? c.darkHex : c.hex,
                        color: ['#F8F7F5', '#F0EEEB', '#FFFFFF', '#FEFEFE', '#F0E8FA', '#FFFBEB', '#E6F5F3', '#E8F0FA', '#FBE9E9'].includes(c.hex)
                          ? '#4A4540' : '#F5F0EC'
                      }}
                    >
                      {isDark && c.darkHex ? c.darkHex : c.hex}
                    </div>
                    <div className="loc-swatch-info">
                      <div className="loc-swatch-name">{c.name}</div>
                      <div className="loc-swatch-desc">{c.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Typography */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Typography</h2>
            <p className="loc-section-desc">
              All sans-serif and monospace — no serifs. Space Grotesk brings personality to headings,
              Inter handles body text, and JetBrains Mono is used creatively throughout UI elements.
            </p>
          </div>

          <div className="loc-type-grid">
            {typographyTokens.map((t, i) => (
              <div key={i} className="loc-type-card">
                <div className="loc-type-header">
                  <div className="loc-type-name" style={{ fontFamily: t.family }}>{t.name}</div>
                  <span className="loc-type-usage">{t.usage}</span>
                </div>
                <div className="loc-type-sample" style={{ fontFamily: t.family }}>{t.sample}</div>
                <div className="loc-type-meta">
                  <div className="loc-type-weights">
                    {t.weights.map((w, j) => <span key={j} className="loc-type-weight">{w}</span>)}
                  </div>
                  <span className="loc-type-family">{t.family.split(',')[0].replace(/'/g, '')}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Type Scale */}
          <div style={{ marginTop: 32 }}>
            <div className="loc-subsection-title">Type Scale</div>
            <div className="loc-scale-samples">
              {[
                { size: 42, label: '42px — Page title', family: "'Space Grotesk', sans-serif", weight: 700 },
                { size: 30, label: '30px — Section heading', family: "'Space Grotesk', sans-serif", weight: 700 },
                { size: 22, label: '22px — Card heading', family: "'Space Grotesk', sans-serif", weight: 600 },
                { size: 16, label: '16px — Body large', family: "'Inter', sans-serif", weight: 400 },
                { size: 15, label: '15px — Body', family: "'Inter', sans-serif", weight: 400 },
                { size: 13, label: '13px — Caption', family: "'Inter', sans-serif", weight: 500 },
                { size: 11, label: '11px — Mono label', family: "'JetBrains Mono', monospace", weight: 600 },
              ].map((s, i) => (
                <div key={i} className="loc-scale-item">
                  <span className="loc-scale-text" style={{ fontSize: s.size, fontFamily: s.family, fontWeight: s.weight }}>{s.size}</span>
                  <span className="loc-scale-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Iconography */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Iconography</h2>
            <p className="loc-section-desc">
              Phosphor Icons with solid "fill" weight. Civic-themed icons for government, community, and data.
              Icon previews use all three brand colors for variety.
            </p>
          </div>
          <div className="loc-icon-grid">
            {iconShowcase.map((ic, i) => (
              <div key={i} className="loc-icon-item">
                <div className="loc-icon-item-preview">{ic.icon}</div>
                <div className="loc-icon-item-info">
                  <div className="loc-icon-item-name">{ic.name}</div>
                  <div className="loc-icon-item-usage">{ic.usage}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SVG Patterns */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Patterns</h2>
            <p className="loc-section-desc">
              Three SVG-based patterns add texture and uniqueness. One bold diagonal hash and two subtle
              patterns (dots, grid) used tastefully in backgrounds, headers, and emphasized elements.
            </p>
          </div>
          <div className="loc-pattern-showcase">
            <div className="loc-pattern-card loc-pattern-bold" style={{ backgroundColor: 'var(--loc-purple-bg)' }}>
              <span className="loc-pattern-card-label">Bold — Diagonal Hash</span>
            </div>
            <div className="loc-pattern-card loc-pattern-dots" style={{ backgroundColor: 'var(--loc-amber-bg)' }}>
              <span className="loc-pattern-card-label">Subtle — Dots</span>
            </div>
            <div className="loc-pattern-card loc-pattern-grid" style={{ backgroundColor: 'var(--loc-teal-bg)' }}>
              <span className="loc-pattern-card-label">Subtle — Grid</span>
            </div>
          </div>
        </section>

        {/* Spacing & Radius */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Spacing & Radius</h2>
            <p className="loc-section-desc">
              Consistent spacing creates rhythm. Mixed corner radii — mostly sharp, with selective rounding
              for toggles and pills. No excessive rounding.
            </p>
          </div>
          <div className="loc-spacing-grid">
            <div className="loc-spacing-card">
              <div className="loc-subsection-title">Spacing Scale</div>
              <div className="loc-space-samples">
                {[4, 8, 12, 16, 20, 24, 32, 40, 48, 64].map((size) => (
                  <div key={size} className="loc-space-item">
                    <div className="loc-space-bar" style={{ width: size }} />
                    <span className="loc-space-label">{size}px</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="loc-spacing-card">
              <div className="loc-subsection-title">Border Radius</div>
              <div className="loc-radius-samples">
                {[
                  { val: 0,   token: '--loc-radius-none',  label: '0px — Sharp (default)' },
                  { val: 4,   token: '--loc-radius-xs',    label: '4px — Tight' },
                  { val: 6,   token: '--loc-radius-sm',    label: '6px — Small' },
                  { val: 10,  token: '--loc-radius-md',    label: '10px — Medium' },
                  { val: 14,  token: '--loc-radius-lg',    label: '14px — Large' },
                  { val: 18,  token: '--loc-radius-xl',    label: '18px — XLarge' },
                  { val: 999, token: '--loc-radius-pill',   label: '999px — Pill' },
                ].map((r, i) => (
                  <div key={i} className="loc-radius-item">
                    <div className="loc-radius-box" style={{ borderRadius: r.val }} />
                    <div className="loc-radius-label">
                      <code>{r.token}</code> {r.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Shadows */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Shadows</h2>
            <p className="loc-section-desc">
              Hard-edged, no-blur box shadows for a bold, graphic feel. Colored variants use brand
              colors for emphasis on interactive elements.
            </p>
          </div>
          <div className="loc-shadow-grid">
            {[
              { name: 'XS',      token: '--loc-shadow-xs',     style: '2px 2px 0 rgba(28,25,23,0.06)' },
              { name: 'SM',      token: '--loc-shadow-sm',     style: '3px 3px 0 rgba(28,25,23,0.08)' },
              { name: 'MD',      token: '--loc-shadow-md',     style: '5px 5px 0 rgba(28,25,23,0.10)' },
              { name: 'LG',      token: '--loc-shadow-lg',     style: '8px 8px 0 rgba(28,25,23,0.12)' },
              { name: 'XL',      token: '--loc-shadow-xl',     style: '12px 12px 0 rgba(28,25,23,0.14)' },
              { name: 'Purple',  token: '--loc-shadow-purple', style: '6px 6px 0 #7B3FD4' },
              { name: 'Amber',   token: '--loc-shadow-amber',  style: '6px 6px 0 #D69E2E' },
              { name: 'Teal',    token: '--loc-shadow-teal',   style: '6px 6px 0 #2A9D8F' },
            ].map((s, i) => (
              <div
                key={i}
                className="loc-shadow-card"
                style={{ boxShadow: s.style }}
              >
                <div className="loc-shadow-name">{s.name}</div>
                <div className="loc-shadow-token">{s.token}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Buttons */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Buttons</h2>
            <p className="loc-section-desc">
              Bold fills with sharp corners and hard-edged shadows. Purple primary for main CTAs, vibrant amber
              and teal for secondary emphasis. No borders on filled buttons.
            </p>
          </div>

          <div className="loc-button-row">
            <div className="loc-button-row-label">{'// variants'}</div>
            <div className="loc-button-grid">
              <button className="loc-btn loc-btn-primary"><Newspaper weight="fill" size={16} /> Read Policy</button>
              <button className="loc-btn loc-btn-secondary"><MapPin weight="fill" size={16} /> My District</button>
              <button className="loc-btn loc-btn-ghost"><Info weight="fill" size={16} /> Learn More</button>
              <button className="loc-btn loc-btn-amber"><Flag weight="fill" size={16} /> Register</button>
              <button className="loc-btn loc-btn-teal"><Gavel weight="fill" size={16} /> Vote Info</button>
              <button className="loc-btn loc-btn-destructive"><XCircle weight="fill" size={16} /> Unsubscribe</button>
            </div>
          </div>

          <div className="loc-button-row">
            <div className="loc-button-row-label">{'// sizes'}</div>
            <div className="loc-button-grid">
              <button className="loc-btn loc-btn-primary loc-btn-sm">Small</button>
              <button className="loc-btn loc-btn-primary">Default</button>
              <button className="loc-btn loc-btn-primary loc-btn-lg">Large</button>
              <button className="loc-btn loc-btn-primary" disabled>Disabled</button>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Cards</h2>
            <p className="loc-section-desc">
              Content containers with bold color-blocked accents. Sharp corners with hard-edged shadows.
              Colored shadows on hover for interactive personality.
            </p>
          </div>
          <div className="loc-card-grid">
            <div className="loc-card">
              <div className="loc-card-title">Default Card</div>
              <div className="loc-card-text">
                Standard container for static content like council meeting summaries or district demographic data.
              </div>
              <div className="loc-card-meta">
                <span className="loc-card-tag">Static</span>
                <span className="loc-card-date">no interaction</span>
              </div>
            </div>

            <div className="loc-card loc-card-interactive">
              <div className="loc-card-title">Interactive Card</div>
              <div className="loc-card-text">
                Clickable cards for navigation — policy detail pages, council member profiles, or event pages.
              </div>
              <div className="loc-card-meta">
                <span className="loc-card-tag">Clickable</span>
                <span className="loc-card-date"><ArrowRight size={14} /></span>
              </div>
            </div>

            <div className="loc-card loc-card-elevated">
              <div className="loc-card-title">Elevated Card</div>
              <div className="loc-card-text">
                Pre-elevated cards for featured content — breaking policy updates or upcoming election reminders.
              </div>
              <div className="loc-card-meta">
                <span className="loc-card-tag" style={{ background: 'var(--loc-amber)' }}>Featured</span>
                <span className="loc-card-date">highlighted</span>
              </div>
            </div>

            <div className="loc-card loc-card-glow">
              <div className="loc-card-title">Accent Card</div>
              <div className="loc-card-text">
                Teal accent treatment for the most important elements — active vote alerts or urgent policy changes.
              </div>
              <div className="loc-card-meta">
                <span className="loc-card-tag" style={{ background: 'var(--loc-teal)' }}>Urgent</span>
                <span className="loc-card-date">teal accent</span>
              </div>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Form Elements</h2>
            <p className="loc-section-desc">
              Clean inputs with sharp corners and 2px borders. Hard-edged focus shadows.
              Monospace hints and labels create a distinct civic-tech feel.
            </p>
          </div>

          <div className="loc-form-grid">
            <div className="loc-form-group">
              <label className="loc-form-label">Street Address</label>
              <input className="loc-input" type="text" placeholder="123 Main Street, Nashville" readOnly />
              <span className="loc-form-hint">{'// used to determine your council district'}</span>
            </div>

            <div className="loc-form-group">
              <label className="loc-form-label">District</label>
              <select className="loc-select" defaultValue="">
                <option value="" disabled>Select your district</option>
                <option>District 1 — Germantown</option>
                <option>District 5 — East Nashville</option>
                <option>District 17 — Sylvan Park</option>
                <option>District 34 — Bellevue</option>
              </select>
            </div>

            <div className="loc-form-group">
              <label className="loc-form-label">Email (error state)</label>
              <input className="loc-input loc-input-error" type="email" defaultValue="not-valid" readOnly />
              <span className="loc-form-hint" style={{ color: 'var(--loc-error)' }}>{'// please enter a valid email address'}</span>
            </div>

            <div className="loc-form-group">
              <label className="loc-form-label">Feedback</label>
              <textarea className="loc-textarea" placeholder="Tell us how we can improve..." readOnly />
            </div>
          </div>

          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="loc-toggle-group" onClick={() => toggleSwitch('notifications')}>
              <div className={`loc-toggle-track${toggleStates.notifications ? ' active' : ''}`}>
                <div className="loc-toggle-knob" />
              </div>
              <span className="loc-toggle-label">Push notifications for my district</span>
            </div>
            <div className="loc-toggle-group" onClick={() => toggleSwitch('digest')}>
              <div className={`loc-toggle-track${toggleStates.digest ? ' active' : ''}`}>
                <div className="loc-toggle-knob" />
              </div>
              <span className="loc-toggle-label">Weekly policy digest email</span>
            </div>
          </div>
        </section>

        {/* Alerts */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Alerts</h2>
            <p className="loc-section-desc">
              Contextual messages with bold left-border accents, sharp corners, and monospace titles. Clear, skimmable status at a glance.
            </p>
          </div>
          <div className="loc-alerts-grid">
            <div className="loc-alert loc-alert-success">
              <span className="loc-alert-icon"><CheckCircle weight="fill" size={18} /></span>
              <div className="loc-alert-content">
                <div className="loc-alert-title">registration_confirmed</div>
                <div className="loc-alert-text">You're registered to vote in Davidson County, District 5.</div>
              </div>
            </div>
            <div className="loc-alert loc-alert-warning">
              <span className="loc-alert-icon"><Warning weight="fill" size={18} /></span>
              <div className="loc-alert-content">
                <div className="loc-alert-title">voter_deadline_approaching</div>
                <div className="loc-alert-text">Register by October 7 to vote in the November general election.</div>
              </div>
            </div>
            <div className="loc-alert loc-alert-error">
              <span className="loc-alert-icon"><XCircle weight="fill" size={18} /></span>
              <div className="loc-alert-content">
                <div className="loc-alert-title">address_not_found</div>
                <div className="loc-alert-text">We couldn't match your address to a council district. Please check and try again.</div>
              </div>
            </div>
            <div className="loc-alert loc-alert-info">
              <span className="loc-alert-icon"><Info weight="fill" size={18} /></span>
              <div className="loc-alert-content">
                <div className="loc-alert-title">council_meeting_scheduled</div>
                <div className="loc-alert-text">Metro Council meets Tuesday at 6:30 PM. Agenda includes BL2026-101 (transit funding).</div>
              </div>
            </div>
          </div>
        </section>

        {/* Badges */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Badges & Tags</h2>
            <p className="loc-section-desc">
              Monospace-powered labels with solid fills and sharp corners. Bold, readable status indicators that pop against any background.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div className="loc-subsection-title">Status Badges</div>
              <div className="loc-badges-row">
                <span className="loc-badge loc-badge-purple">Active</span>
                <span className="loc-badge loc-badge-amber">Pending</span>
                <span className="loc-badge loc-badge-teal">In Committee</span>
                <span className="loc-badge loc-badge-success">Passed</span>
                <span className="loc-badge loc-badge-error">Defeated</span>
                <span className="loc-badge loc-badge-info">Deferred</span>
                <span className="loc-badge loc-badge-warning">Amended</span>
                <span className="loc-badge loc-badge-outline">Draft</span>
              </div>
            </div>
            <div>
              <div className="loc-subsection-title">Topic Tags</div>
              <div className="loc-badges-row">
                <span className="loc-badge loc-badge-purple"><MapPin weight="fill" size={12} /> Zoning</span>
                <span className="loc-badge loc-badge-amber"><Buildings weight="fill" size={12} /> Housing</span>
                <span className="loc-badge loc-badge-teal"><Scales weight="fill" size={12} /> Budget</span>
                <span className="loc-badge loc-badge-blue"><Globe weight="fill" size={12} /> Transit</span>
                <span className="loc-badge loc-badge-success"><Leaf weight="fill" size={12} /> Environment</span>
                <span className="loc-badge loc-badge-outline"><ShieldCheck weight="fill" size={12} /> Public Safety</span>
              </div>
            </div>
          </div>
        </section>

        {/* Page Example: Policy Feed (with pattern in header) */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Page Example — Policy Feed</h2>
            <p className="loc-section-desc">
              The AI-managed policy feed is the primary interface. Sharp-cornered cards with pattern-textured headers
              and hard-edged shadows make each card instantly scannable.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="loc-feed-card">
              <div className="loc-feed-header loc-pattern-dots">
                <div>
                  <div className="loc-feed-title">BL2026-101: Nashville Transit Expansion</div>
                  <div className="loc-feed-date">
                    <CalendarBlank weight="fill" size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                    {"filed 2026-03-04 // council_member: O'Connell"}
                  </div>
                </div>
                <span className="loc-feed-status loc-badge-purple">Active</span>
              </div>
              <div className="loc-feed-body">
                <div className="loc-feed-summary">
                  Proposes $2.1B funding for expanded bus rapid transit corridors along Gallatin Pike,
                  Nolensville Pike, and Charlotte Ave. Includes dedicated bus lanes, 47 new shelters,
                  and real-time tracking integration. Funded through a combination of federal grants
                  and a 0.5% sales tax referendum.
                </div>
                <div className="loc-feed-tags">
                  <span className="loc-badge loc-badge-blue"><Globe weight="fill" size={12} /> Transit</span>
                  <span className="loc-badge loc-badge-amber"><Scales weight="fill" size={12} /> Budget</span>
                  <span className="loc-badge loc-badge-outline">District 5</span>
                  <span className="loc-badge loc-badge-outline">District 7</span>
                </div>
              </div>
              <div className="loc-feed-footer">
                <button className="loc-btn loc-btn-ghost loc-btn-sm">Share</button>
                <button className="loc-btn loc-btn-primary loc-btn-sm">Read Full Bill <ArrowRight size={14} /></button>
              </div>
            </div>

            <div className="loc-feed-card">
              <div className="loc-feed-header loc-pattern-grid" style={{ backgroundColor: 'var(--loc-teal-bg)' }}>
                <div>
                  <div className="loc-feed-title">RS2026-42: Affordable Housing Density Bonus</div>
                  <div className="loc-feed-date">
                    <CalendarBlank weight="fill" size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                    {"filed 2026-02-18 // council_member: Withers"}
                  </div>
                </div>
                <span className="loc-feed-status loc-badge-teal">In Committee</span>
              </div>
              <div className="loc-feed-body">
                <div className="loc-feed-summary">
                  Resolution to allow increased building density in exchange for affordable housing
                  set-asides. Developers can build 20% additional units if 15% are reserved at 80% AMI
                  or below. Applies to Urban Zoning Overlay districts.
                </div>
                <div className="loc-feed-tags">
                  <span className="loc-badge loc-badge-amber"><Buildings weight="fill" size={12} /> Housing</span>
                  <span className="loc-badge loc-badge-purple"><MapPin weight="fill" size={12} /> Zoning</span>
                </div>
              </div>
              <div className="loc-feed-footer">
                <button className="loc-btn loc-btn-ghost loc-btn-sm">Share</button>
                <button className="loc-btn loc-btn-teal loc-btn-sm">Read Full Resolution <ArrowRight size={14} /></button>
              </div>
            </div>
          </div>
        </section>

        {/* Page Example: Stat Metrics */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Page Example — District Dashboard Metrics</h2>
            <p className="loc-section-desc">
              Data-forward metric cards with color-block top accents and colored hard shadows on hover. Quick stats residents can scan at a glance.
            </p>
          </div>

          <div className="loc-metrics-row">
            <div className="loc-metric-card">
              <div className="loc-metric-card-label">Active Bills</div>
              <div className="loc-metric-card-value">24</div>
              <div className="loc-metric-card-change up">+3 this month</div>
            </div>
            <div className="loc-metric-card">
              <div className="loc-metric-card-label">Days to Election</div>
              <div className="loc-metric-card-value">142</div>
              <div className="loc-metric-card-change" style={{ color: 'var(--loc-text-muted)' }}>Nov 3, 2026</div>
            </div>
            <div className="loc-metric-card">
              <div className="loc-metric-card-label">Council Attendance</div>
              <div className="loc-metric-card-value">87%</div>
              <div className="loc-metric-card-change down">-2% vs last quarter</div>
            </div>
            <div className="loc-metric-card">
              <div className="loc-metric-card-label">Public Comments</div>
              <div className="loc-metric-card-value">156</div>
              <div className="loc-metric-card-change up">+22% this month</div>
            </div>
          </div>
        </section>

        {/* Page Example: Voter Assistance */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Page Example — Voter Assistance</h2>
            <p className="loc-section-desc">
              Step-by-step voter guidance with bold purple header blocks and numbered steps using all brand colors.
            </p>
          </div>

          <div className="loc-voter-card">
            <div className="loc-voter-header loc-pattern-bold">
              <div className="loc-voter-header-icon">
                <ShieldCheck weight="fill" size={24} />
              </div>
              <div>
                <div className="loc-voter-header-title">Register to Vote in Nashville</div>
                <div className="loc-voter-header-subtitle">{'davidson_county // tennessee'}</div>
              </div>
            </div>
            <div className="loc-voter-body">
              <div className="loc-voter-step">
                <div className="loc-voter-step-num">1</div>
                <div className="loc-voter-step-text">
                  <strong>Check eligibility.</strong> You must be a U.S. citizen, Tennessee resident,
                  and at least 18 years old by Election Day.
                </div>
              </div>
              <div className="loc-voter-step">
                <div className="loc-voter-step-num">2</div>
                <div className="loc-voter-step-text">
                  <strong>Complete the form.</strong> Register online at GoVoteTN.com or pick up a paper
                  form at any public library or the Election Commission office.
                </div>
              </div>
              <div className="loc-voter-step">
                <div className="loc-voter-step-num">3</div>
                <div className="loc-voter-step-text">
                  <strong>Submit by the deadline.</strong> Your registration must be received at least
                  30 days before the election. Online forms must be submitted by 11:59 PM.
                </div>
              </div>
              <div className="loc-voter-step">
                <div className="loc-voter-step-num">4</div>
                <div className="loc-voter-step-text">
                  <strong>Bring valid photo ID.</strong> Tennessee requires government-issued photo ID to
                  vote. A driver's license, passport, or state-issued voter ID card are accepted.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Page Example: District Map Placeholder (with pattern background) */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Page Example — District Map</h2>
            <p className="loc-section-desc">
              Interactive maps for council district boundaries, polling locations, and geographic policy impact.
            </p>
          </div>

          <div className="loc-map-placeholder loc-pattern-dots">
            <div className="loc-map-placeholder-icon">
              <MapTrifold weight="fill" size={48} />
            </div>
            <div className="loc-map-placeholder-title">Nashville Council Districts</div>
            <p>Interactive map component — renders Mapbox/Leaflet with GeoJSON district boundaries, council member overlays, and address-to-district lookup.</p>
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <span className="loc-badge loc-badge-purple">35 Districts</span>
              <span className="loc-badge loc-badge-amber">Vice Mayor At-Large</span>
              <span className="loc-badge loc-badge-teal">5 At-Large Seats</span>
            </div>
          </div>
        </section>

        {/* Token Reference */}
        <section className="loc-section">
          <div className="loc-section-header">
            <h2 className="loc-section-title">Token Reference</h2>
            <p className="loc-section-desc">
              Quick reference for all design tokens. Use these CSS custom properties for consistent theming across all Localite pages.
            </p>
          </div>
          <div className="loc-token-table-wrap">
            <table className="loc-token-table">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Light</th>
                  <th>Dark</th>
                  <th>Usage</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { token: '--loc-bg',             light: '#F8F7F5', dark: '#171412', usage: 'Page background' },
                  { token: '--loc-surface',         light: '#FFFFFF', dark: '#2A2724', usage: 'Card / panel background' },
                  { token: '--loc-border',          light: '#D8D4CE', dark: '#4A4540', usage: 'Default border' },
                  { token: '--loc-text',            light: '#1C1917', dark: '#F5F0EC', usage: 'Primary text' },
                  { token: '--loc-text-secondary',  light: '#4A4540', dark: '#C8C0B8', usage: 'Secondary text' },
                  { token: '--loc-text-muted',      light: '#8A8580', dark: '#8A8580', usage: 'Muted / hint text' },
                  { token: '--loc-purple',          light: '#7B3FD4', dark: '#B07EF7', usage: 'Primary accent' },
                  { token: '--loc-purple-vibrant',  light: '#9333EA', dark: '#C084FC', usage: 'Vibrant primary' },
                  { token: '--loc-amber',           light: '#D69E2E', dark: '#F6C745', usage: 'Secondary accent' },
                  { token: '--loc-amber-vibrant',   light: '#F59E0B', dark: '#FBBF24', usage: 'Vibrant amber' },
                  { token: '--loc-teal',            light: '#2A9D8F', dark: '#5EEAD4', usage: 'Secondary accent' },
                  { token: '--loc-teal-vibrant',    light: '#14B8A6', dark: '#2DD4BF', usage: 'Vibrant teal' },
                  { token: '--loc-blue',            light: '#3B6FB6', dark: '#6A9BD4', usage: 'Tertiary — civic blue' },
                  { token: '--loc-red',             light: '#C5393A', dark: '#F87171', usage: 'Tertiary — civic red' },
                  { token: '--loc-shadow-purple',   light: 'Purple hard shadow', dark: 'Purple hard shadow', usage: 'Featured elements' },
                ].map((row, i) => (
                  <tr key={i}>
                    <td><code>{row.token}</code></td>
                    <td>{row.light}</td>
                    <td>{row.dark}</td>
                    <td>{row.usage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </div>

      {/* ============ FOOTER (with bold pattern) ============ */}
      <footer className="loc-footer loc-pattern-bold">
        <div className="loc-footer-mark">Localite</div>
        {'design_system // v2.1 // 2026'}
      </footer>

    </div>
  );
};

export default LocaliteDesignSystem;
